import { useTranslation } from 'next-i18next';

import { LanguageId, SupportedSourceLanguages } from '~/constants/languages';
import { useSupportedLanguages } from '~/hooks/useSupportedLanguages';
import { useUpdateUserPersonalizationMutation, LanguageId as ServerLanguageId } from '~/networking/schema';

import { useLanguageInfo } from './useLanguageInfo';
import { useLoggedInUser } from './useLoggedInUser';

interface Option {
  label: string;
  value: LanguageId;
}

export const useLanguageSelector = () => {
  const { getLanguageFromLanguageId, getDefaultLanguageInfo } = useLanguageInfo();
  const { i18n } = useTranslation();
  const { targetLanguage, sourceLanguage, refreshUser } = useLoggedInUser();
  const [updatePersonalization] = useUpdateUserPersonalizationMutation();
  const { supportedLanguages } = useSupportedLanguages();

  const getSelectOptionFromLanguageId = (id: LanguageId): Option => ({
    label: getLanguageFromLanguageId(id) ?? 'None',
    value: id,
  });

  const targetLanguageOptions = supportedLanguages[sourceLanguage as SupportedSourceLanguages].map((id) =>
    getSelectOptionFromLanguageId(id as LanguageId),
  );

  const sourceLanguageOptions = Object.keys(supportedLanguages).map((id) =>
    getSelectOptionFromLanguageId(id as LanguageId),
  );

  const updateSourceLanguage = async (languageId: SupportedSourceLanguages) => {
    if (!supportedLanguages[languageId].includes(targetLanguage)) {
      await updatePersonalization({
        variables: {
          input: {
            language: getDefaultLanguageInfo(languageId).key,
            sourceLanguage: (languageId as unknown) as ServerLanguageId,
          },
        },
      });
      i18n.changeLanguage(languageId);
    } else {
      await updatePersonalization({
        variables: {
          input: { sourceLanguage: (languageId as unknown) as ServerLanguageId, language: targetLanguage },
        },
      });
    }
    await refreshUser();
  };

  const updateTargetLanguage = async (languageId: LanguageId) => {
    const language = getLanguageFromLanguageId(languageId)?.toLowerCase();
    await updatePersonalization({
      variables: {
        input: { language, sourceLanguage: (sourceLanguage as unknown) as ServerLanguageId },
      },
    });
    await refreshUser();
  };

  return {
    targetLanguage,
    sourceLanguage,
    sourceLanguageOptions,
    targetLanguageOptions,
    updateSourceLanguage,
    updateTargetLanguage,
  };
};
